/**
 * Excel Parser Worker
 * 
 * This module provides a Web Worker-based Excel parser that runs off the main thread
 * to prevent UI freezing when parsing large Excel files (5k-15k+ rows).
 * 
 * Uses inline worker pattern compatible with Create React App (no ejection required).
 * 
 * Column mappings are injected from the shared config (src/config/columnMappings.js)
 * to maintain a single source of truth across the application.
 */

import { REQUIRED_COLUMNS, KNOWN_COLUMNS } from '../config/columnMappings';

/**
 * Generate the worker code with injected column mappings.
 * This ensures the worker uses the same mappings as the main thread.
 */
const generateWorkerCode = () => {
  // Serialize the mappings as JSON for injection into the worker
  const requiredColumnsJson = JSON.stringify(REQUIRED_COLUMNS);
  const knownColumnsJson = JSON.stringify(KNOWN_COLUMNS);
  
  return `
  // Import ExcelJS in the worker (will be loaded via importScripts)
  importScripts('https://cdn.jsdelivr.net/npm/exceljs@4.4.0/dist/exceljs.min.js');
  
  // Column mappings injected from src/config/columnMappings.js
  // This ensures frontend and worker stay in sync
  const REQUIRED_COLUMNS = ${requiredColumnsJson};
  const KNOWN_COLUMNS = ${knownColumnsJson};

  self.onmessage = async function(e) {
    const { type, payload } = e.data;
    
    if (type === 'parse') {
      try {
        const { buffer, existingColumnMappings } = payload;
        
        self.postMessage({ type: 'progress', message: 'Loading Excel file...', percent: 5 });
        
        // Validate file signature
        const uint8Array = new Uint8Array(buffer);
        if (uint8Array[0] !== 0x50 || uint8Array[1] !== 0x4B) {
          throw new Error('File does not appear to be a valid Excel .xlsx file');
        }
        
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        
        self.postMessage({ type: 'progress', message: 'Finding Import sheet...', percent: 15 });
        
        // Find the 'import' sheet
        const worksheet = workbook.worksheets.find(
          ws => ws && ws.name && ws.name.toLowerCase() === 'import'
        );
        
        if (!worksheet) {
          const availableSheets = workbook.worksheets
            .filter(ws => ws && ws.name)
            .map(ws => ws.name)
            .join(', ');
          throw new Error('Sheet named "Import" not found. Available sheets: ' + (availableSheets || 'none'));
        }
        
        self.postMessage({ type: 'progress', message: 'Reading headers...', percent: 20 });
        
        // Get headers
        const headerRow = worksheet.getRow(1);
        const headers = [];
        headerRow.eachCell((cell, colNumber) => {
          headers[colNumber - 1] = cell.value ? String(cell.value).trim() : '';
        });
        
        // Fill gaps
        const maxCol = headerRow.cellCount;
        for (let i = 0; i < maxCol; i++) {
          if (headers[i] === undefined) headers[i] = '';
        }
        
        // Check for UUID column
        const uuidIndex = headers.findIndex(h => h && h.trim().toLowerCase() === 'uuid');
        const hasUuid = uuidIndex !== -1;
        
        self.postMessage({ type: 'progress', message: 'Parsing rows...', percent: 25 });
        
        // Parse data rows with progress updates
        const dataRows = [];
        const totalRows = worksheet.rowCount - 1;
        let lastProgressUpdate = 25;
        
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return; // Skip header
          
          const rowData = [];
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const cellValue = cell.value;
            if (cellValue === null || cellValue === undefined) {
              rowData[colNumber - 1] = null;
            } else if (typeof cellValue === 'object' && cellValue.result !== undefined) {
              rowData[colNumber - 1] = cellValue.result !== null ? String(cellValue.result) : null;
            } else {
              rowData[colNumber - 1] = String(cellValue);
            }
          });
          
          // Fill gaps
          for (let i = 0; i < headers.length; i++) {
            if (rowData[i] === undefined) rowData[i] = null;
          }
          
          if (rowData.some(cell => cell !== null && cell !== '')) {
            dataRows.push(rowData);
          }
          
          // Update progress every 1000 rows
          if (dataRows.length % 1000 === 0) {
            const progress = 25 + Math.floor((dataRows.length / totalRows) * 50);
            if (progress > lastProgressUpdate) {
              lastProgressUpdate = progress;
              self.postMessage({ 
                type: 'progress', 
                message: 'Parsing rows... ' + dataRows.length.toLocaleString() + ' rows', 
                percent: progress 
              });
            }
          }
        });
        
        self.postMessage({ type: 'progress', message: 'Matching columns...', percent: 80 });
        
        // Match columns
        const matches = {};
        const matchedIndices = new Set();
        
        // Add UUID if present
        if (hasUuid) {
          matches['UUID'] = { found: true, excelColumn: headers[uuidIndex], dbColumn: 'uuid', index: uuidIndex };
          matchedIndices.add(uuidIndex);
        }
        
        // Match known columns
        Object.keys(KNOWN_COLUMNS).forEach(knownCol => {
          const foundIndex = headers.findIndex(h => h && h.trim().toLowerCase() === knownCol.toLowerCase());
          if (foundIndex !== -1) {
            const dbColumn = KNOWN_COLUMNS[knownCol];
            const alreadyMatched = Object.values(matches).some(m => m.found && m.dbColumn === dbColumn);
            if (!alreadyMatched) {
              matches[knownCol] = { found: true, excelColumn: headers[foundIndex], dbColumn, index: foundIndex };
              matchedIndices.add(foundIndex);
            } else {
              matchedIndices.add(foundIndex); // Mark alias as matched too
            }
          }
        });
        
        // Check required columns (only if no UUID)
        const unmatchedRequired = [];
        if (!hasUuid) {
          Object.keys(REQUIRED_COLUMNS).forEach(requiredCol => {
            const dbColumn = REQUIRED_COLUMNS[requiredCol];
            const isMatched = Object.values(matches).some(m => m.found && m.dbColumn === dbColumn);
            if (!isMatched) unmatchedRequired.push(requiredCol);
          });
        }
        
        // Find unmapped columns
        const extraColumns = [];
        const autoMappedCustomColumns = {};
        
        headers.forEach((header, index) => {
          if (header && !matchedIndices.has(index)) {
            // Check existing custom column mappings
            const existingMapping = (existingColumnMappings || []).find(
              m => m.custom_name && m.custom_name.toLowerCase() === header.toLowerCase()
            );
            
            if (existingMapping) {
              matches[header] = {
                found: true,
                excelColumn: header,
                dbColumn: existingMapping.column_key,
                index: index,
                isCustomColumn: true
              };
              matchedIndices.add(index);
              autoMappedCustomColumns[header] = {
                targetColumn: existingMapping.column_key,
                customName: existingMapping.custom_name,
                isEditable: existingMapping.is_editable
              };
            } else {
              extraColumns.push({ excelHeader: header, index });
            }
          }
        });
        
        self.postMessage({ type: 'progress', message: 'Finalizing...', percent: 95 });
        
        // Return results
        self.postMessage({
          type: 'result',
          data: {
            headers,
            dataRows,
            matches,
            hasUuid,
            uuidIndex,
            unmatchedRequired,
            extraColumns,
            autoMappedCustomColumns,
            rowCount: dataRows.length
          }
        });
        
      } catch (error) {
        self.postMessage({ type: 'error', error: error.message });
      }
    }
  };
`;
};

/**
 * Creates a Web Worker for parsing Excel files
 * @returns {Worker} The web worker instance
 */
export function createExcelParserWorker() {
  const workerCode = generateWorkerCode();
  const blob = new Blob([workerCode], { type: 'application/javascript' });
  const workerUrl = URL.createObjectURL(blob);
  const worker = new Worker(workerUrl);
  
  // Clean up the blob URL when worker is terminated
  const originalTerminate = worker.terminate.bind(worker);
  worker.terminate = () => {
    URL.revokeObjectURL(workerUrl);
    originalTerminate();
  };
  
  return worker;
}

/**
 * Parse an Excel file using a Web Worker
 * @param {File} file - The Excel file to parse
 * @param {Array} existingColumnMappings - Existing custom column mappings for the project
 * @param {Function} onProgress - Progress callback (message, percent)
 * @returns {Promise<Object>} Parsed data including headers, rows, matches, etc.
 */
export function parseExcelWithWorker(file, existingColumnMappings = [], onProgress = () => {}) {
  return new Promise((resolve, reject) => {
    const worker = createExcelParserWorker();
    
    worker.onmessage = (e) => {
      const { type, data, message, percent, error } = e.data;
      
      switch (type) {
        case 'progress':
          onProgress(message, percent);
          break;
        case 'result':
          worker.terminate();
          resolve(data);
          break;
        case 'error':
          worker.terminate();
          reject(new Error(error));
          break;
        default:
          break;
      }
    };
    
    worker.onerror = (error) => {
      worker.terminate();
      reject(new Error('Worker error: ' + error.message));
    };
    
    // Read file as ArrayBuffer and send to worker
    file.arrayBuffer().then(buffer => {
      worker.postMessage({
        type: 'parse',
        payload: { buffer, existingColumnMappings }
      }, [buffer]); // Transfer the buffer for efficiency
    }).catch(reject);
  });
}

/**
 * Batch data for upload
 * @param {Array} data - The full data array
 * @param {number} batchSize - Size of each batch
 * @returns {Array<Array>} Array of batches
 */
export function batchData(data, batchSize = 1000) {
  const batches = [];
  for (let i = 0; i < data.length; i += batchSize) {
    batches.push(data.slice(i, i + batchSize));
  }
  return batches;
}
